import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { arrayOf, bool, func, number, shape, string } from "prop-types";
import { connect } from "react-redux";
import { compose } from "redux";
import { push } from "react-router-redux";
import { Redirect } from "react-router-dom";

import { Box, CreateCredin } from "@ui-kit/components/index.es";

import { resizableRoute } from "pages/_components/Resizable";

import { actions as accountsActions, selectors as accountsSelectors } from "reducers/accounts";
import { actions as debinActions, selectors as debinSelectors } from "reducers/debin";
import { actions as formActions, selectors as formSelectors } from "reducers/form";
import {
    actions as frequentDestinationActions,
    selectors as frequentDestinationSelectors,
} from "reducers/frequentDestination";
import { actions as notificationActions } from "reducers/notification";
import { selectors as sessionSelectors } from "reducers/session";
import { actions as statusActions } from "reducers/status";
import { actions as transferActions, selectors as transferSelectors } from "reducers/transfer";

import * as config from "util/config";
import { calculateTotalAmountCbuList, getCredinActivityId } from "util/credin";
import resolveDraftTransactionListGoBack from "util/draftTransactionListGoBack";
import * as i18n from "util/i18n";
import { useEmbeddedV3ShellLayoutSync, isCredinV3Enabled } from "util/v3ShellLayoutSync";
import { REQUEST_CREDIN_LEGACY_PATH } from "util/v3UxConfig";

import {
    buildCredinRecipientSearchCallback,
    findCredinAccount,
    findEmptyCredinRecipientField,
    formatCredinLimitAmount,
    getCreateCredinLabel,
    getCredinCurrencyLabel,
    hasCredinSignatureLimitError,
    isCredinContinueInactive,
    mapCbuListToRecipientEntries,
    mapCredinAgendaContacts,
    mapCredinConcepts,
    mapCredinDebitAccounts,
    mapRecipientEntriesToCbuList,
    parseCredinAmount,
    resolveCredinConceptId,
} from "v3/pages/transfers/createCredin";

const CREDIN_SCOPES = ["credin"];
const CREDIN_GO_BACK_PATH = "/v3/dashboard";
const CREDIN_ACTIVITY = "credin.sentRequest.send";
const CREDIN_CVU_ACTIVITY = "credinCVU.sentRequest.send";
const DEFAULT_CREDIN_CONCEPT_ID = "VAR";

/**
 * V3 Credin creation screen. Renders the UI-kit `CreateCredin` component while delegating every
 * business operation to the existing legacy `debin` slice (concepts, limits, recipients preview,
 * draft). The recipients edited in the UI-kit `RecipientsArrayField` (Formik) are mapped back to the
 * legacy `debin.cbuList` shape on submit, so the summary/OTP/confirmation flow stays untouched.
 */
function CreateCredinV3Page(props) {
    const {
        dispatch,
        isDesktop,
        accounts,
        loggedUser,
        concepts,
        fetching,
        fetchingDraft,
        fetchingLimits,
        limit,
        limitUsed,
        limits,
        seller,
        cbuList,
        recipients,
        isBackFromSummary,
        idEnvironment,
        listFrequentDestinations,
        activeEnvironment,
        embeddedInV3Shell,
        location,
    } = props;

    const isCredinCVUSignatureActive = config.getBoolean("frontend.esquemaFirmas.validation.credinCVU.enable", false);
    const extendedAmountEnabled = config.getBoolean("increase.size.amount.link.iso8583", false);
    const canCreateDraft = Boolean(activeEnvironment && activeEnvironment.permissions.createDraft);
    const canUseAgenda = Boolean(activeEnvironment && activeEnvironment.permissions.frequentDestination);

    const [selectedAccountId, setSelectedAccountId] = useState(isBackFromSummary && seller ? seller.account || "" : "");
    const [isPreviewing, setIsPreviewing] = useState(false);
    const [areAccountLimitsReady, setAreAccountLimitsReady] = useState(false);
    const formikRef = useRef(null);
    const limitToastAllowedRef = useRef(true);
    const limitToastTimerRef = useRef(null);
    const limitsFetchAccountRef = useRef(null);
    const restoredFormDataRef = useRef(isBackFromSummary && seller ? { seller, cbuList } : null);
    const restoredFormData = restoredFormDataRef.current;
    // Capture once: leftover `returnToPending` from a previous draft must not affect a new CREDIN.
    const draftGoBackPathRef = useRef(
        isBackFromSummary && seller?.returnToPending
            ? seller.draftTransactionListGoBackPath || resolveDraftTransactionListGoBack("")
            : null,
    );
    const goBackPath = draftGoBackPathRef.current || CREDIN_GO_BACK_PATH;

    const conceptOptions = useMemo(() => mapCredinConcepts(concepts), [concepts]);
    const restoredConceptId = resolveCredinConceptId(restoredFormData?.seller?.concept);
    const restoredConceptOption = useMemo(
        () => conceptOptions.find(({ value }) => String(value) === restoredConceptId),
        [conceptOptions, restoredConceptId],
    );
    const defaultConceptOption = useMemo(
        () => conceptOptions.find(({ value }) => String(value).toUpperCase() === DEFAULT_CREDIN_CONCEPT_ID),
        [conceptOptions],
    );

    // The preview (preGenerateCredin) toggles `debin.fetching`; clear the Continue spinner when it
    // settles so the button re-enables if the backend rejected the request.
    useEffect(() => {
        if (!fetching) {
            setIsPreviewing(false);
        }
    }, [fetching]);

    useEffect(() => {
        if (!selectedAccountId) {
            limitsFetchAccountRef.current = null;
            setAreAccountLimitsReady(false);
            return;
        }
        if (fetchingLimits) {
            limitsFetchAccountRef.current = selectedAccountId;
            setAreAccountLimitsReady(false);
            return;
        }
        if (limitsFetchAccountRef.current === selectedAccountId) {
            setAreAccountLimitsReady(true);
        }
    }, [fetchingLimits, selectedAccountId]);

    /** Restored values keep the option shape expected by SelectFieldV3, as transfer forms do. */
    const initialValues = useMemo(() => {
        if (restoredFormData) {
            return {
                debitAccount: restoredFormData.seller.account || "",
                concept: restoredConceptOption || "",
                description: restoredFormData.seller.description || "",
                recipients: mapCbuListToRecipientEntries(restoredFormData.cbuList),
            };
        }
        return { debitAccount: "", concept: defaultConceptOption || "", description: "", recipients: [] };
    }, [defaultConceptOption, restoredConceptOption, restoredFormData]);

    // Initial data load — mirrors legacy RequestCredin.componentDidMount (no business logic change).
    useEffect(() => {
        // Records this screen as the flow origin so the ticket back button returns here instead of the
        // list visited before entering. Skip in draft mode so Volver can keep the pending/historic list.
        if (location && !draftGoBackPathRef.current) {
            dispatch(statusActions.saveLastHref(location));
        }
        dispatch(debinActions.clearRequestData());
        if (!(accounts && accounts.length > 0) && loggedUser && loggedUser.userId) {
            dispatch(accountsActions.listAccounts(loggedUser.userId));
        }
        if (!isBackFromSummary) {
            dispatch(debinActions.setCbuSelected([]));
        }
        dispatch(debinActions.debinPreActivity());
        dispatch(transferActions.resetModalAgendaValues());
        dispatch(debinActions.cleanIsBackFromSummary());
        if (activeEnvironment && activeEnvironment.permissions && activeEnvironment.permissions.frequentDestination) {
            dispatch(frequentDestinationActions.loadListRequest(idEnvironment, null));
        }
        dispatch(transferActions.preActivity());
        return () => {
            if (limitToastTimerRef.current) {
                clearTimeout(limitToastTimerRef.current);
            }
        };
    }, []);

    // v3 shell header sync (title / back / scopes). On desktop the page renders its own header via
    // `CreateCredin` (goBack); on mobile the unified shell header shows the title + back.
    useEmbeddedV3ShellLayoutSync(dispatch, {
        embeddedInV3Shell,
        isDesktop,
        titleKey: "requestCredin.title",
        goBackPath,
        scopes: CREDIN_SCOPES,
    });

    const debitAccountOptions = useMemo(() => mapCredinDebitAccounts(accounts), [accounts]);
    const selectedAccount = useMemo(() => findCredinAccount(accounts, selectedAccountId), [
        accounts,
        selectedAccountId,
    ]);
    const agendaContacts = useMemo(() => (canUseAgenda ? mapCredinAgendaContacts(listFrequentDestinations) : []), [
        canUseAgenda,
        listFrequentDestinations,
    ]);
    const recipientsSearchCallback = useMemo(() => buildCredinRecipientSearchCallback(), []);
    const isSignatureLimitCheckEnabled =
        isCredinCVUSignatureActive && Boolean(selectedAccountId) && areAccountLimitsReady && !fetchingLimits;

    const signatureLimitValidation = useMemo(() => {
        const limitList = Array.isArray(limits) ? limits : [];
        const cbuLimit = limitList.find((limitObj) => limitObj.type === "CBU");
        const cvuLimit = limitList.find((limitObj) => limitObj.type === "CVU");
        return {
            enabled: isSignatureLimitCheckEnabled,
            cbuAvailable: (cbuLimit && cbuLimit.limitUsed) || 0,
            cvuAvailable: (cvuLimit && cvuLimit.limitUsed) || 0,
        };
    }, [isSignatureLimitCheckEnabled, limits]);

    const selectedCurrencyLabel = selectedAccount ? getCredinCurrencyLabel(selectedAccount.currency) : "ARS";

    const handleSetFormikProps = useCallback((formik) => {
        formikRef.current = formik;
    }, []);

    const handleAccountChange = useCallback(
        (accountId) => {
            setSelectedAccountId(accountId);
            setAreAccountLimitsReady(false);
            limitsFetchAccountRef.current = null;
            const account = findCredinAccount(accounts, accountId);
            if (!account) {
                return;
            }
            if (isCredinCVUSignatureActive) {
                dispatch(
                    transferActions.accountLimitsListActivities(account.idProduct, [
                        CREDIN_ACTIVITY,
                        CREDIN_CVU_ACTIVITY,
                    ]),
                );
            } else {
                dispatch(transferActions.accountLimitsActivities(account.idProduct, CREDIN_ACTIVITY));
            }
        },
        [accounts, dispatch, isCredinCVUSignatureActive],
    );

    const handleBack = useCallback(() => {
        dispatch(push(goBackPath));
    }, [dispatch, goBackPath]);

    const renderLimits = useCallback(() => {
        if (!selectedAccountId || fetchingLimits) {
            return null;
        }
        const currencyCode = selectedAccount ? selectedAccount.currency : undefined;
        if (isCredinCVUSignatureActive) {
            if (!Array.isArray(limits) || limits.length === 0) {
                return null;
            }
            return (
                <Box column gap={4}>
                    {limits.map((limitObj) => {
                        const limitLabel = i18n.get(
                            `transfers.limit.credin${limitObj.type}.label`,
                            `Límite a ${limitObj.type}`,
                        );
                        return (
                            <Box key={limitObj.type} flex wrap align="center" gap={8}>
                                <Box>
                                    {limitLabel}: <span>{formatCredinLimitAmount(limitObj.limit, currencyCode)}</span>
                                </Box>
                                <Box aria-hidden>|</Box>
                                <Box>
                                    {i18n.get("transfers.available.label", "Límite disponible")}:{" "}
                                    <span>
                                        {formatCredinLimitAmount(limitObj.limitUsed, currencyCode, {
                                            clampNegativeAvailable: true,
                                        })}
                                    </span>
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            );
        }
        return (
            <Box flex wrap align="center" gap={8}>
                <Box>
                    {i18n.get("transfers.limit.label", "Límite")}:{" "}
                    <span>{formatCredinLimitAmount(limit, currencyCode)}</span>
                </Box>
                <Box aria-hidden>|</Box>
                <Box>
                    {i18n.get("transfers.available.label", "Límite disponible")}:{" "}
                    <span>{formatCredinLimitAmount(limitUsed, currencyCode, { clampNegativeAvailable: true })}</span>
                </Box>
            </Box>
        );
    }, [selectedAccountId, fetchingLimits, isCredinCVUSignatureActive, limits, limit, limitUsed, selectedAccount]);

    const handleLimitErrorChange = useCallback(
        (hasError) => {
            if (!hasError || !limitToastAllowedRef.current) {
                return;
            }
            limitToastAllowedRef.current = false;
            dispatch(
                notificationActions.showNotification(
                    i18n.get("transfers.validation.limits.TAD"),
                    "error",
                    CREDIN_SCOPES,
                ),
            );
            limitToastTimerRef.current = setTimeout(() => {
                limitToastAllowedRef.current = true;
            }, 4000);
        },
        [dispatch],
    );

    const isContinueInactive = useCallback(
        (values) =>
            isCredinContinueInactive(values) ||
            hasCredinSignatureLimitError(values.recipients, limits, isSignatureLimitCheckEnabled),
        [isSignatureLimitCheckEnabled, limits],
    );

    const notifyMissingRecipients = useCallback(
        (field) => {
            const messageKey = field ? `credin.empty.${field}.receivers` : "requestCredin.emptyReceivers.label";
            dispatch(notificationActions.showNotification(i18n.get(messageKey), "error", CREDIN_SCOPES));
        },
        [dispatch],
    );

    const buildSellerAndCbuList = useCallback(
        (values) => {
            const account = findCredinAccount(accounts, values.debitAccount);
            const currencyCode = account ? String(account.currency) : "";
            const nextCbuList = mapRecipientEntriesToCbuList(values.recipients, currencyCode, recipients);
            const nextSeller = {
                account: values.debitAccount,
                concept: resolveCredinConceptId(values.concept),
                description: values.description,
                cbu: account ? account.cbu : undefined,
                idAccount: account ? account.idProduct : undefined,
                idTransaction: seller ? seller.idTransaction : undefined,
                debitAccount: account,
            };
            return { cbuList: nextCbuList, seller: nextSeller };
        },
        [accounts, recipients, seller],
    );

    const handleContinue = useCallback(
        (values, formikBag) => {
            const stopSubmitting = () => {
                if (formikBag && typeof formikBag.setSubmitting === "function") {
                    formikBag.setSubmitting(false);
                }
            };
            const { cbuList: nextCbuList, seller: nextSeller } = buildSellerAndCbuList(values);
            if (nextCbuList.length === 0) {
                notifyMissingRecipients(null);
                stopSubmitting();
                return;
            }
            const emptyField = findEmptyCredinRecipientField(nextCbuList);
            if (emptyField) {
                notifyMissingRecipients(emptyField);
                stopSubmitting();
                return;
            }
            if (hasCredinSignatureLimitError(values.recipients, limits, isSignatureLimitCheckEnabled)) {
                stopSubmitting();
                return;
            }
            setIsPreviewing(true);
            dispatch(debinActions.setCbuSelected(nextCbuList));
            dispatch(debinActions.preGenerateCredin(nextSeller, nextCbuList));
            stopSubmitting();
        },
        [buildSellerAndCbuList, dispatch, isSignatureLimitCheckEnabled, limits, notifyMissingRecipients],
    );

    const handleSaveDraft = useCallback(
        (values) => {
            if (!canCreateDraft) {
                return;
            }
            const { cbuList: nextCbuList, seller: nextSeller } = buildSellerAndCbuList(values);
            if (nextCbuList.length === 0) {
                notifyMissingRecipients(null);
                return;
            }
            const emptyField = findEmptyCredinRecipientField(nextCbuList);
            if (emptyField) {
                notifyMissingRecipients(emptyField);
                return;
            }
            dispatch(debinActions.setCbuSelected(nextCbuList));
            const amount = calculateTotalAmountCbuList(nextCbuList);
            dispatch(
                formActions.saveDraft({
                    idForm: "credinSentRequest",
                    data: {
                        account: values.debitAccount,
                        concept: resolveCredinConceptId(values.concept),
                        description: values.description,
                        cbuList: nextCbuList,
                        amount,
                    },
                    idTransaction: seller && seller.idTransaction ? seller.idTransaction : null,
                    summary: { debinSeller: nextSeller, cbuList: nextCbuList },
                    idActivityDraft: getCredinActivityId(nextCbuList, isCredinCVUSignatureActive),
                }),
            );
        },
        [buildSellerAndCbuList, canCreateDraft, dispatch, notifyMissingRecipients, seller],
    );

    const isInitialDataLoading = fetching && (!(accounts && accounts.length > 0) || !(concepts && concepts.length > 0));

    if (!isCredinV3Enabled()) {
        return <Redirect to={REQUEST_CREDIN_LEGACY_PATH} />;
    }

    return (
        <CreateCredin
            initialValues={initialValues}
            setFormikProps={handleSetFormikProps}
            getFormLabel={getCreateCredinLabel}
            pageTitle={i18n.get("requestCredin.title", "Generar CREDIN")}
            goBack={handleBack}
            loading={isInitialDataLoading}
            loadingSubmit={isPreviewing}
            loadingDraft={fetchingDraft}
            isDraftInactive={(values) => {
                const draftRecipients = Array.isArray(values.recipients) ? values.recipients : [];
                return (
                    !canCreateDraft ||
                    draftRecipients.length === 0 ||
                    draftRecipients.some(
                        (recipient) =>
                            parseCredinAmount(recipient.amount) <= 0 || !String(recipient.comprobante || "").trim(),
                    )
                );
            }}
            debitAccountOptions={debitAccountOptions}
            onDebitAccountChange={handleAccountChange}
            renderLimits={renderLimits}
            recipientsSearchCallback={recipientsSearchCallback}
            agendaContacts={agendaContacts}
            showAgendaToggle={canUseAgenda}
            expandAgenda={false}
            signatureLimitValidation={signatureLimitValidation}
            onLimitErrorChange={handleLimitErrorChange}
            conceptOptions={conceptOptions}
            currency={selectedCurrencyLabel}
            extendedAmountEnabled={extendedAmountEnabled}
            onSubmit={handleContinue}
            onSaveDraft={handleSaveDraft}
            continueExtraInactive={isContinueInactive}
        />
    );
}

CreateCredinV3Page.propTypes = {
    dispatch: func.isRequired,
    isDesktop: bool.isRequired,
    accounts: arrayOf(shape({ idProduct: string })),
    loggedUser: shape({ userId: string }),
    concepts: arrayOf(shape({})),
    fetching: bool,
    fetchingDraft: bool,
    fetchingLimits: bool,
    limit: number,
    limitUsed: number,
    limits: arrayOf(shape({})),
    seller: shape({
        returnToPending: bool,
        draftTransactionListGoBackPath: string,
    }),
    cbuList: arrayOf(shape({})),
    recipients: arrayOf(shape({})),
    isBackFromSummary: bool,
    idEnvironment: number,
    listFrequentDestinations: arrayOf(shape({})),
    activeEnvironment: shape({ permissions: shape({ createDraft: bool, frequentDestination: bool }) }),
    embeddedInV3Shell: bool,
    location: shape({ pathname: string }),
};

CreateCredinV3Page.defaultProps = {
    accounts: [],
    loggedUser: null,
    concepts: [],
    fetching: false,
    fetchingDraft: false,
    fetchingLimits: false,
    limit: 0,
    limitUsed: 0,
    limits: [],
    seller: null,
    cbuList: [],
    recipients: [],
    isBackFromSummary: false,
    idEnvironment: null,
    listFrequentDestinations: [],
    activeEnvironment: null,
    embeddedInV3Shell: false,
    location: null,
};

const mapStateToProps = (state) => ({
    accounts: accountsSelectors.getAccounts(state),
    loggedUser: sessionSelectors.getUser(state),
    seller: debinSelectors.getDebinSeller(state),
    cbuList: debinSelectors.getListCbu(state),
    recipients: debinSelectors.getRecipients(state),
    fetching: debinSelectors.getFetching(state),
    fetchingDraft: formSelectors.getFetchingDraft(state),
    concepts: debinSelectors.getConcepts(state),
    isBackFromSummary: debinSelectors.getIsBackFromSummary(state),
    fetchingLimits: transferSelectors.getFetching(state),
    limit: transferSelectors.getLimit(state),
    limitUsed: transferSelectors.getLimitUsed(state),
    limits: transferSelectors.getLimits(state),
    activeEnvironment: sessionSelectors.getActiveEnvironment(state),
    idEnvironment: frequentDestinationSelectors.getEnviromentId(state),
    listFrequentDestinations: frequentDestinationSelectors.getListFrequentDestinations(state),
});

export default compose(connect(mapStateToProps), resizableRoute)(CreateCredinV3Page);
